import { SchoolIcon } from '@zep-us/zus-icons';

import type { SearchQuizsetRes } from '@/api/gen/_model/searchQuizsetRes';
import {
  ArrowTurnUprightOutlinedIcon,
  ChartLineIcon,
  CheckIcon,
  EnterIcon,
  FolderIcon,
  GoogleClassroomLogoIcon,
  PenPaperIcon,
  SquareStackIcon,
  TrashIcon,
} from '@/icons';
import type ZepIcon from '@/icons/ZepIcon';
import { useExternalPlatform } from '@/shared/externalPlatform';
import { useCanCreateClassroom } from '@/shared/hooks';
import { type QuizListType } from '@/shared/quizList/QuizListType';
import { type CommonTranslationKeys } from '@/types/translation';
import type { ButtonProps } from '@/ui_v3';

const CommonQuizMenuTypes: QuizListType[] = ['public', 'featured', 'user'];

/**
 * 남의 폴더 안 퀴즈 행. 폴더 전체를 재생하는 CTA 대신 퀴즈를 한 개씩 가져가는 버튼이다.
 * `excludeHoverMenu` 를 주지 않아 데스크톱에서는 행 호버, 모바일에서는 케밥 메뉴로 뜬다.
 */
const FolderQuizMenuItems: QuizCardMenuItem[] = [
  {
    label: 'detail.saveToMyQuiz',
    icon: SquareStackIcon,
    variant: 'outlined',
    intent: 'neutral',
    onClickEventName: 'save_to_my_quiz',
    showGoogleClassButton: false,
  },
];

export const useGetQuizListItemButtons = (type: QuizListType) => {
  const { isInGoogleClassroom: inGoogleClassRoom, isInLti: inLti } =
    useExternalPlatform();
  // 학생은 클래스를 만들거나 연결할 수 없다 — 퀴즈 상세 「클래스 연결」과 같은 기준.
  const canLinkClassroom = useCanCreateClassroom();

  return (quizSet: SearchQuizsetRes) => {
    if (inLti) {
      const ltiMenuButtons =
        type === 'myQuiz' ? ltiMyMenuButtons : [LtiSelectMenuItem];

      return quizSet.isLocked
        ? ltiMenuButtons.filter(item => item.onClickEventName !== 'lti_select')
        : ltiMenuButtons;
    }

    if (CommonQuizMenuTypes.includes(type)) {
      return [];
    }

    // `folder` 는 남이 보는 폴더 상세다(소유자는 `myQuiz` 메뉴를 받는다).
    if (type === 'folder') {
      return FolderQuizMenuItems;
    }

    const googleMyMenuButtons: QuizCardMenuItem[] = [
      {
        label: 'googleClassroom.createAssignmentButton.text',
        icon: GoogleClassroomLogoIcon,
        intent: 'neutral',
        onClickEventName: 'google_assignment',
      },
    ];

    return inGoogleClassRoom
      ? googleMyMenuButtons
      : MyQuizCardMenuItems.filter(
          d =>
            d.showGoogleClassButton === inGoogleClassRoom &&
            (canLinkClassroom || d.onClickEventName !== 'link_classroom'),
        ).map(d => ({
          ...d,
          disabled: d.onClickEventName === 'enter' ? quizSet.isLocked : false,
        }));
  };
};

export const LtiSelectMenuItem: QuizCardMenuItem = {
  label: 'moodle.selectQuizButton.text',
  icon: CheckIcon,
  intent: 'neutral',
  onClickEventName: 'lti_select',
};

export const MyQuizEditMenuItem: QuizCardMenuItem = {
  label: 'global.edit.button',
  icon: PenPaperIcon,
  onClickEventName: 'update',
  excludeHoverMenu: true,
  showGoogleClassButton: false,
};

const ltiMyMenuButtons: QuizCardMenuItem[] = [
  MyQuizEditMenuItem,
  LtiSelectMenuItem,
];

export const MyQuizCardMenuItems: QuizCardMenuItem[] = [
  {
    label: 'global.enter.button',
    icon: EnterIcon,
    onClickEventName: 'enter',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.share.button',
    icon: ArrowTurnUprightOutlinedIcon,
    onClickEventName: 'share',
    intent: 'neutral',
    variant: 'contained',
    showGoogleClassButton: false,
  },
  /** 퀴즈 상세 「클래스 연결」과 같은 연결 창을 연다. 행 호버 버튼에는 넣지 않는다. */
  {
    label: 'classroom.linkToClassroom',
    icon: SchoolIcon,
    onClickEventName: 'link_classroom',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.duplicate.button',
    icon: SquareStackIcon,
    variant: 'contained',
    intent: 'neutral',
    onClickEventName: 'copy',
    showGoogleClassButton: false,
  },
  MyQuizEditMenuItem,
  /** 폴더로 이동. 퀴즈는 최대 한 폴더에만 속하므로 "담기"가 아니라 이동이다. */
  {
    label: 'detail.moveToFolder',
    icon: FolderIcon,
    variant: 'contained',
    intent: 'neutral',
    onClickEventName: 'move_to_folder',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
  {
    label: 'lmsBtn',
    icon: ChartLineIcon,
    onClickEventName: 'report',
    variant: 'outlined',
    intent: 'alternative',
    hidePortableDevice: true,
    showGoogleClassButton: false,
  },
  {
    label: 'global.delete',
    icon: TrashIcon,
    onClickEventName: 'delete',
    excludeHoverMenu: true,
    showGoogleClassButton: false,
  },
];

export type QuizCardMenuItem = {
  icon: ReturnType<typeof ZepIcon>;
  label: CommonTranslationKeys;
  variant?: ButtonProps['variant'];
  intent?: ButtonProps['intent'];
  disabled?: boolean;
  onClickEventName:
    | 'share'
    | 'link_classroom'
    | 'copy'
    | 'report'
    | 'enter'
    | 'update'
    | 'delete'
    | 'move_to_folder'
    /** 남의 퀴즈를 내 쪽으로 복사한다. `copy` 와 달리 빌드 페이지를 거치지 않는다. */
    | 'save_to_my_quiz'
    | 'google_assignment'
    | 'lti_select';
  excludeHoverMenu?: boolean;
  hidePortableDevice?: boolean;
  showGoogleClassButton?: boolean;
};
